import { VIEW_ICONS } from '@baserow/modules/database/viewIcons'

/**
 * Resolution of a view's own icon and colour, with the fallback to its view
 * type. This module is the single place the fallback rule lives: every render
 * surface routes through it, so "custom icon wins, absent icon falls back to
 * the type icon" cannot drift between the rail, the collapsed rail and the
 * header.
 *
 * `View.icon` and `View.color` are `CharField`s that default to `""` (see
 * `backend/src/baserow/contrib/database/views/models.py`), so "unset" is an
 * empty string rather than `null` or an absent key. Both are treated
 * independently: colouring the type icon is a valid way to tell six grid views
 * apart and must not require also picking an icon.
 */

/**
 * The full CSS class for a view's icon. Prefers the view's own `icon` from the
 * curated catalogue, and falls back to the icon of the view type so that "is
 * this a grid or a gallery?" stays answerable for every view that has not been
 * given an identity.
 */
export function getViewIconClass(view) {
  const icon = view?.icon
  if (icon) {
    return `iconoir-${icon}`
  }
  return view?._?.type?.iconClass || ''
}

/**
 * The full CSS class for a view's icon colour. Every iconoir glyph is a
 * `currentColor` mask, so setting `color` tints the icon with no per-icon
 * styling.
 *
 * `view-color--<name>`, not the `color--<name>` this used to return. Those are
 * the record-colouring classes, drawn from `$colors`, and every one of them is
 * a background tint: measured against white, all 27 names a view can be given
 * land between 1.03:1 and 2.30:1 -- `light-orange` is #fffbf0, white on white.
 * They are correct behind a record and unusable as an icon.
 *
 * `view-color--*` keeps the same names, so stored `View.color` values and the
 * backend's `BASEROW_COLORS` validation are untouched, and maps each to a tint
 * of the same hue that can actually be seen at icon size.
 */
export function getViewColorClass(view) {
  const color = view?.color
  if (color) {
    return `view-color--${color}`
  }
  return view?._?.type?.colorClass || ''
}

/**
 * True when the view carries an icon of its own, i.e. when the type icon is no
 * longer visible on the row. Callers use this to keep the view type
 * discoverable somewhere else, such as in the row's tooltip.
 */
export function viewHasCustomIcon(view) {
  return Boolean(view?.icon)
}

/**
 * True when `icon` is a member of the curated catalogue. A value that is not
 * would be rejected by the API, so the picker never offers one.
 */
export function isValidViewIcon(icon) {
  return VIEW_ICONS.includes(icon)
}
